import React, { useState, useEffect } from 'react';
import { Triangle } from 'react-loader-spinner';
import { useNavigate } from 'react-router-dom';
import DiscountService from '../../../Services/QRCodeServices/DiscountService/DiscountService';
import CustomDataTable from '../../../Components/CustomTable/CustomTable';
import { EditDiscount } from './EditDiscount';
import DeleteModal from '../../../Components/Common/DeleteModal';
import StatusToggleModal from './StatusToggleModal'; // Import the StatusToggleModal
import { Link } from 'react-router-dom';

export const DiscountDataTable = ({ refreshDiscounts, searchQuery }) => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalData: 0,
  });
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [selectedDiscountId, setSelectedDiscountId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [clickedRowId, setClickedRowId] = useState(null);
  const [statusModal, setStatusModal] = useState(false); // State for status toggle modal
  const [selectedDiscountForStatus, setSelectedDiscountForStatus] = useState(null); // Selected discount for status toggle

  const navigate = useNavigate();

  useEffect(() => {
    fetchDiscounts();
  }, [pagination.currentPage, pagination.pageSize, refreshDiscounts]);

  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      const response = await DiscountService.getList();
      setDiscounts(response || []);
      setPagination((prev) => ({ ...prev, totalData: response.length }));
      setError(null);
    } catch (err) {
      setError('Failed to fetch discounts');
      console.error('Error fetching discounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPagination((prev) => ({ ...prev, pageSize: newPerPage, currentPage: page }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleEdit = (id) => {
    setSelectedDiscountId(id);
    setIsEditPopupOpen(true);
  };

  const handleDeleteClick = (id) => {
    setSelectedDeleteItem(id);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await DiscountService.delete(selectedDeleteItem);
      refreshDiscounts();
      setDeleteModal(false);
    } catch (error) {
      console.error('Error deleting discount:', error);
    }
  };

  const handleTitleClick = (id) => {
    setClickedRowId(id);
    navigate(`/discount-detail/${id}`);
  };

  // Open status toggle modal
  const handleStatusClick = (discount) => {
    setSelectedDiscountForStatus(discount);
    setStatusModal(true);
  };

  // Filter discounts based on search query
  const filteredDiscounts = discounts.filter((discount) =>
    discount.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns = [
    {
      name: 'S.N',
      selector: (row, index) => (pagination.currentPage - 1) * pagination.pageSize + index + 1,
      sortable: true,
      width: '75px',
    },
    // {
    //   name: 'Title',
    //   cell: (row) => (
    //     <span
    //       className={`text-primary cursor-pointer ${clickedRowId === row.id ? 'bg-primary text-white' : ''}`}
    //       style={{ textDecoration: 'underline', padding: '5px', borderRadius: '5px' }}
    //       onClick={() => handleTitleClick(row.id)}
    //     >
    //       {row.title}
    //     </span>
    //   ),
    //   sortable: true,
    // },
    {
      name: "Title",
      cell: (row) => <Link to={`/discount-detail/${row.id}`}>{row.title}</Link>,
      sortable: true,
    },
    {
      name: 'Description',
      selector: (row) => row.description,
      sortable: true,
      width: '140px',
    },
    {
      name: 'Start Date',
      selector: (row) => new Date(row.startDate).toLocaleDateString(),
      sortable: true,
    },
    {
      name: 'End Date',
      selector: (row) => new Date(row.endDate).toLocaleDateString(),
      sortable: true,
    },
    {
      name: 'Percentage',
      selector: (row) => `${row.discountPercentage}%`,
      sortable: true,
    },
    {
      name: 'Status',
      cell: (row) => (
        <span
          className={`badge cursor-pointer ${row.isActive ? 'bg-success' : 'bg-danger'}`}
          onClick={() => handleStatusClick(row)}
        >
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
      sortable: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            className="btn d-flex align-items-center justify-content-center btn-warning btn-sm"
            onClick={() => handleEdit(row.id)}
          >
            <i className="bx bx-edit"></i>
          </button>
          <button
            className="btn d-flex align-items-center justify-content-center btn-danger btn-sm"
            onClick={() => handleDeleteClick(row.id)}
          >
            <i className="bx bx-trash"></i>
          </button>
        </div>
      ),
      center: true,
    },
  ];

  if (loading) {
    return (
      <div className="text-center h-100 w-100 d-flex justify-content-center align-items-center p-5">
        <Triangle visible={true} height="80" width="80" color="#5B71B9" ariaLabel="triangle-loading" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-danger p-3">{error}</div>;
  }

  return (
    <>
      <CustomDataTable
        columns={columns}
        data={filteredDiscounts}
        pagination
        paginationServer
        paginationTotalRows={pagination.totalData}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        fixedHeader
        persistTableHead
        progressPending={loading}
        customStyles={{
          headCells: { style: { fontSize: '0.93rem', fontWeight: 610 } },
          rows: { style: { fontSize: '0.9rem' } },
        }}
        progressComponent={
          <div className="d-flex justify-content-center align-items-center text-center h-100 w-100">
            <Triangle visible={true} height="80" width="80" color="#5B71B9" ariaLabel="triangle-loading" />
            <h5 className="mt-1">Loading...</h5>
          </div>
        }
      />
      <EditDiscount
        isOpen={isEditPopupOpen}
        toggle={() => setIsEditPopupOpen(false)}
        discountId={selectedDiscountId}
        refreshDiscounts={refreshDiscounts}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        selectedItem={selectedDeleteItem}
      />
      <StatusToggleModal
        isOpen={statusModal}
        toggle={() => setStatusModal(false)}
        discount={selectedDiscountForStatus}
        refreshDiscounts={refreshDiscounts}
      />
    </>
  );
};